<template>
  <div
    class="gmb-url-attributes-mass-edit"
    :class="{ disabled: !canEditFacilityAttributes }"
  >
    <v-row class="max-w-560">
      <v-col class="d-flex align-center pb-0 max-w-50px">
        <v-checkbox
          class="mt-0"
          v-model="checkbox"
          @change="onCheckboxClicked"
          :disabled="!canEditFacilityAttributes"
        ></v-checkbox>
      </v-col>
      <v-col class="d-flex align-center pb-0">
        <paragraph class="border-accent">
          {{ $t("message.facilityDetails.urlAttributes.facilitiesAttributes") }}
        </paragraph>
      </v-col>
    </v-row>
    <v-row v-if="!urlAttributes.length" class="position-relative">
      <v-col class="max-w-50px" />
      <v-col>
        <div class="description">
          {{ $t("message.facilityDetails.urlAttributes.noAttributesSelected") }}
        </div>
        <InfoBox
          class="mt-2"
          :text="$t('message.facilityDetails.urlAttributes.editMessage')"
        />
        <div class="actions" v-if="checkbox">
          <div class="action" v-for="action in actions" :key="action.type">
            <div class="description muted">
              {{ $t(action.msg) }}
            </div>
            <v-btn v-bind="action.btnProps" @click="handleAction(action.type)">
              {{ $t(action.btnLabel) }}
            </v-btn>
            <v-tooltip right :max-width="tooltipMaxWidth">
              <template v-slot:activator="{ on }">
                <div class="info-icon" v-on="on">
                  <v-icon color="lime">mdi-information</v-icon>
                </div>
              </template>
              {{ $t(action.desc) }}
            </v-tooltip>
          </div>
        </div>
      </v-col>
    </v-row>
    <template v-if="urlAttributes.length">
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <div class="active-action">
            <v-row>
              <v-col cols="11" class="d-flex align-center">
                <span class="section-title">{{ activeActionLabel }}:</span>
              </v-col>
              <v-col cols="1">
                <v-btn
                  icon
                  color="primary"
                  @click="handleAction(selectedActionType)"
                >
                  <v-icon>
                    icon-edit
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <UrlAttributesPreview :url-attributes="urlAttributes" />

          <div class="change-action" v-if="selectedActionType">
            <div class="description muted mb-0">
              {{ $t("message.facilityDetails.urlAttributes.changeType") }}
            </div>
            <v-btn
              v-for="action in notActiveActions"
              :key="action.type"
              text
              color="primary"
              @click="changeAction(action.type)"
              class="ml-4"
            >
              {{ $t(action.btnLabel) }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <UrlAttributesDialog
      :is-open="dialog"
      :facilities-ids="facilitiesIds"
      :initial-url-attributes="urlAttributes"
      :delete-mode="deleteMode"
      :title="selectedAction && selectedAction.btnLabel"
      :description="selectedAction && selectedAction.desc"
      @close="dialog = false"
      @save="handleSave"
    />
  </div>
</template>

<script>
import Paragraph from "@/components/ui/Paragraph";
import UrlAttributesPreview from "@/components/FacilityDetails/GmbUrlAttributes/UrlAttributesPreview";
import UrlAttributesDialog from "@/components/FacilityDetails/GmbUrlAttributes/UrlAttributesDialog";
import InfoBox from "@/components/ui/InfoBox.vue";

import { DialogHelper } from "@/helpers/dialog-helper";
import { GmbActionType } from "@/enums/gmb-attribute-action-type";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";

export default {
  name: "UrlAttributesMassEdit",
  components: {
    Paragraph,
    UrlAttributesPreview,
    UrlAttributesDialog,
    InfoBox
  },
  mixins: [permissionsInfo],
  data() {
    return {
      dialog: false,
      checkbox: false,
      urlAttributes: [],
      tooltipMaxWidth: 436,
      actions: [],
      facilitiesIds: [],
      actionTypes: GmbActionType,
      selectedAction: null,
      selectedActionType: null
    };
  },
  computed: {
    activeActionLabel() {
      const labelKey = "message.facilityDetails.urlAttributes.";
      if (this.selectedActionType === this.actionTypes.ADD) {
        return this.$t(labelKey + "addingAttributes");
      } else if (this.selectedActionType === this.actionTypes.DELETE) {
        return this.$t(labelKey + "deletingAttributes");
      } else {
        return "";
      }
    },
    notActiveActions() {
      return this.actions.filter(
        ({ type }) => type !== this.selectedActionType
      );
    },
    deleteMode() {
      return (
        this.selectedAction && this.selectedAction.type === GmbActionType.DELETE
      );
    }
  },
  mounted() {
    this.facilitiesIds = this.$route.params.ids.split(",");
  },
  created() {
    this.setActions();
  },
  methods: {
    setActions() {
      this.actions = [
        {
          type: this.actionTypes.ADD,
          msg: "message.facilityDetails.urlAttributes.addAttributesQuestion",
          desc:
            "message.facilityDetails.urlAttributes.addAttributesDescription",
          btnLabel: "message.facilityDetails.urlAttributes.addAttributes",
          btnProps: {
            color: "primary"
          }
        },
        {
          type: this.actionTypes.DELETE,
          msg: "message.facilityDetails.urlAttributes.deleteAttributesQuestion",
          desc:
            "message.facilityDetails.urlAttributes.deleteAttributesDescription",
          btnLabel: "message.facilityDetails.urlAttributes.deleteAttributes",
          btnProps: {
            color: "primary"
          }
        }
      ];
    },
    async onCheckboxClicked(newVal) {
      this.$emit("update:checked", newVal);
      if (!newVal) {
        this.urlAttributes = [];
        this.selectedAction = null;
        this.selectedActionType = null;
        this.$emit("update:urlAttributes", this.urlAttributes);
      }
    },
    changeAction(actionType) {
      DialogHelper.showConfirmDialog(
        {
          text: this.$t(
            "message.facilityDetails.urlAttributes.changeTypeConfirm"
          ),
          okLabelKey: "message.common.continue"
        },
        () => {
          this.handleAction(actionType);
        }
      );
    },
    handleAction(actionType) {
      if (this.selectedAction && this.selectedAction.type !== actionType) {
        this.urlAttributes = [];
      }

      this.selectedAction = this.actions.find(
        action => action.type === actionType
      );

      this.dialog = true;
    },
    handleSave(urlAttributes) {
      this.urlAttributes = urlAttributes;
      this.selectedActionType = this.selectedAction.type;
      this.$emit("update:type", this.selectedActionType);
      this.$emit("update:urlAttributes", this.urlAttributes);
    }
  }
};
</script>

<style lang="scss" scoped>
.gmb-url-attributes-mass-edit {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding: $spacing * 1.5 0;
}

.section-title {
  @include font-size(16px);
  line-height: 24px;
  font-weight: 600;
  color: $grey-darker;
}

.description {
  @include font-size(14px);
  line-height: 17px;
  color: $grey-darker;
  margin-bottom: $spacing;

  &.muted {
    line-height: 19px;
    opacity: 0.7;
  }
}

.actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-evenly;
  margin-right: -$spacing * 5.75;

  .action {
    flex-basis: 170px;
    position: relative;
    margin-top: $spacing * 5;
    margin-right: $spacing * 5.75;

    .description {
      text-align: center;
      margin-bottom: $spacing * 2.5;
    }

    .info-icon {
      display: inline;
      position: absolute;
      top: 6px;
      right: -30px;
    }
  }

  .v-btn {
    width: 100%;
  }
}

.active-action {
  border-bottom: 1px solid $light-3;
}

.change-action {
  display: flex;
  align-items: center;
  margin-top: $spacing * 2.5;
  padding-top: $spacing;
  border-top: 1px solid $light-3;
}

.max-w-560 {
  max-width: 560px;
}

.border-accent {
  @include borderAccent($lime);
}

.v-btn {
  text-transform: none;
}
</style>
